// ===== TYPE DEFINITIONS =====

interface ReconciliationServerData {
  reconciliation_id?: string | null;
  reconciliation_date?: string | null;
  amount?: string | number | null;
}

interface ReconciliationNode {
  reconciliation_id: string;
  reconciliation_date: string;
  amount: string | number;
}

type AmountType = 'positive' | 'negative' | 'zero';

// ===== MAIN CLASS =====

class ReconciliationModel {
  private _serverData: ReconciliationServerData;

  // ===== PUBLIC PROPERTIES =====
  
  public reconciliationId!: string | null;
  public reconciliationDate!: string | null;
  public amount!: string | number | null;

  constructor(data: ReconciliationServerData = {}) {
    this._serverData = data;
    this._initializeProperties();
  }

  // ===== STATIC FACTORY METHODS =====
  
  static fromServerData(data: ReconciliationServerData): ReconciliationModel {
    return new ReconciliationModel(data);
  }

  static fromReconciliationNode(reconciliationNode: ReconciliationNode | null): ReconciliationModel | null {
    if (!reconciliationNode) return null;
    
    return new ReconciliationModel({
      reconciliation_id: reconciliationNode.reconciliation_id,
      reconciliation_date: reconciliationNode.reconciliation_date,
      amount: reconciliationNode.amount
    });
  }

  // ===== PROPERTY INITIALIZATION =====
  
  private _initializeProperties(): void {
    // Core reconciliation properties from the API response
    this.reconciliationId = this._serverData.reconciliation_id || null;
    this.reconciliationDate = this._serverData.reconciliation_date || null;
    this.amount = this._serverData.amount || null;
  }

  // ===== GETTERS =====
  
  get hasReconciliation(): boolean {
    return !!this.reconciliationId;
  }

  get formattedAmount(): string {
    if (!this.amount) return '$0.00';
    
    const numAmount = parseFloat(String(this.amount));
    if (isNaN(numAmount)) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(numAmount);
  }

  get amountValue(): number {
    if (!this.amount) return 0;
    const numAmount = parseFloat(String(this.amount));
    return isNaN(numAmount) ? 0 : numAmount;
  }

  get isPositive(): boolean {
    return this.amountValue > 0;
  }

  get isNegative(): boolean {
    return this.amountValue < 0;
  }

  get isZero(): boolean {
    return this.amountValue === 0;
  }

  get amountType(): AmountType {
    if (this.isPositive) return 'positive';
    if (this.isNegative) return 'negative';
    return 'zero';
  }

  // ===== VALIDATION METHODS =====
  
  isValid(): boolean {
    return !!(
      this.reconciliationId &&
      this.reconciliationDate &&
      this.amount !== null &&
      this.amount !== undefined
    );
  }

  // ===== UTILITY METHODS =====
  
  toServerData(): ReconciliationServerData {
    return {
      reconciliation_id: this.reconciliationId,
      reconciliation_date: this.reconciliationDate,
      amount: this.amount
    };
  }

  toJSON(): ReconciliationServerData {
    return this.toServerData();
  }

  clone(): ReconciliationModel {
    return new ReconciliationModel(this._serverData);
  }

  // ===== COMPARISON METHODS =====
  
  equals(other: ReconciliationModel): boolean {
    if (!(other instanceof ReconciliationModel)) return false;
    return this.reconciliationId === other.reconciliationId;
  }

  // ===== FORMATTING METHODS =====
  
  getDisplayDate(): string {
    if (!this.reconciliationDate) return 'N/A';
    
    try {
      return new Date(this.reconciliationDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return this.reconciliationDate;
    }
  }

  getShortDate(): string {
    if (!this.reconciliationDate) return 'N/A';
    
    try {
      return new Date(this.reconciliationDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return this.reconciliationDate;
    }
  }
}

export default ReconciliationModel;
export type { ReconciliationServerData, ReconciliationNode, AmountType }; 